// El acomodo del lienzo — ADR-2026-09-16-el-lienzo-acomoda-solo-y-lo-guardado-manda y
// ADR-2026-09-16-las-fotos-del-lienzo-van-sueltas.
//
// Puro y determinista: tandas + posiciones guardadas → nodos de React Flow. Las fotos van
// SUELTAS (no hay marco por tanda): la tanda sólo decide dónde caen las que no tienen
// posición guardada —en grilla, y la tanda entera a la DERECHA de todo lo ya colocado,
// guardado o automático, así una generación nueva aparece "al costado de las últimas"
// aunque el usuario haya movido las anteriores—. Lo guardado manda siempre.
//
// Una tanda con `origenId` (Carrusel, Animar, Expansión pedidos desde el visor sobre una
// foto del lienzo) no va a la derecha: cae DEBAJO de esa foto, alineada con su borde
// izquierdo, por debajo de todo lo que ya ocupa esa franja vertical —así dos carruseles
// de la misma foto se apilan y no se pisan—
// (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo). Si la foto de origen no
// está en el lienzo (se borró el mensaje), va a la derecha como cualquier otra.
//
// Ese lugar se decide UNA vez, al pedir la tanda, y viaja guardado en ella (`posicion`,
// que el lienzo calcula con `lugarDeTanda` sobre lo que ve en ese momento): con él, la
// tanda no vuelve a mirar a su foto de origen. Sin eso las celdas seguían a la foto cada
// vez que la movían —"pegadas", con la posición vinculada— hasta que las movieran una por
// una (ADR-2026-09-17-la-tanda-aterriza-una-vez). Las tandas de los mensajes no tienen
// `posicion` (no se guardan aparte) y siguen cayendo a la derecha de todo, en orden.
//
// Y lo que todavía es automático se CONGELA en cuanto el usuario arrastra u ordena algo
// (`posicionesSueltas`, que el lienzo guarda junto con lo movido): una celda sin posición
// guardada se recalculaba a partir de las demás en cada acomodo —debajo de su foto de
// origen, o a la derecha de todo—, así que mover una foto arrastraba con ella a las que
// nunca habían tocado, como si estuvieran vinculadas
// (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
//
// La proporción de cada celda: la real de la imagen cuando se sabe (`ratios`, por id),
// y si no la del formato pedido a la tanda. De esa proporción salen el ancho Y el alto,
// repartiendo siempre la MISMA superficie
// (ADR-2026-09-22-las-celdas-del-lienzo-ocupan-la-misma-superficie): una vertical sale
// angosta y alta, una apaisada ancha y baja, y las dos pesan lo mismo a la vista. Las
// filas de una grilla miden lo que su celda más alta, y cada columna arranca donde
// termina la anterior —no hay un paso fijo, porque ya no hay un ancho común—. El rótulo
// con el título NO ocupa lugar: `NodoCelda` lo dibuja en la capa de React Flow que no se
// escala con el zoom, como la barra de acciones.

import type { TandaDelLienzo } from './tandasDelLienzo';

/** El ancho de una celda 4:5 —el formato que más se genera—, que es la medida de
 *  siempre. Sirve además de ancho NOMINAL donde hay que estimar antes de conocer las
 *  proporciones (dónde cae una tanda que todavía no llegó). */
export const ANCHO_CELDA = 220;
export const SEPARACION_CELDAS = 12;
export const COLUMNAS_MAX = 4;
export const SEPARACION_TANDAS = 48;
const RATIO_RESPALDO = 0.8;

export interface Punto {
  x: number;
  y: number;
}
export type Posiciones = Record<string, Punto>;
/** Proporción (ancho / alto) conocida de una imagen, por su id. */
export type Ratios = Record<string, number>;

export type DatosCelda = {
  tandaId: string;
  imageId: string | null;
  esVideo: boolean;
  ratio: number;
  creadaEn: number;
  /** El de la celda si tiene, si no el de la tanda. */
  titulo: string;
  /**
   * Una imagen que NO es fila de `imagenes_generadas` (las propias del modo Diseño:
   * bases, inspiraciones, texturas), ya firmada o pública. Con `url` y sin `imageId` la
   * celda se dibuja con ella; sin ninguna de las dos es un recuadro en vuelo
   * (ADR-2026-09-17-el-lienzo-presta-tres-ranuras).
   */
  url?: string;
};

export interface NodoDelLienzo {
  id: string;
  type: 'celda';
  position: Punto;
  style: { width: number; height: number };
  data: DatosCelda;
}

/** El id de un recuadro en vuelo: el mismo que arman `tandasDelLienzo` para la celda `i`. */
export const idDePendiente = (tandaId: string, i: number): string => `${tandaId}:pendiente:${i}`;

/**
 * La superficie que ocupa CUALQUIER celda: la de una 4:5 de 220 de ancho (60.500), así
 * que el formato más común queda exactamente como estaba y ninguna foto se achica.
 * Antes el ancho era fijo y sólo el alto seguía a la proporción: una 4:5 ocupaba dos
 * veces y media lo que una 2:1 y se veía mucho más grande al lado
 * (ADR-2026-09-22-las-celdas-del-lienzo-ocupan-la-misma-superficie).
 */
export const AREA_CELDA = Math.round(ANCHO_CELDA * (ANCHO_CELDA / RATIO_RESPALDO));
/** Topes de ancho: una panorámica no se lleva la fila entera y una tira no queda un hilo.
 *  Recortan la superficie, nunca la proporción. */
export const ANCHO_MINIMO_CELDA = 120;
export const ANCHO_MAXIMO_CELDA = 380;

const ratioUsable = (ratio: number): number =>
  Number.isFinite(ratio) && ratio > 0 ? ratio : RATIO_RESPALDO;

export function anchoDeCelda(ratio: number): number {
  const ancho = Math.sqrt(AREA_CELDA * ratioUsable(ratio));
  return Math.round(Math.min(ANCHO_MAXIMO_CELDA, Math.max(ANCHO_MINIMO_CELDA, ancho)));
}

export function altoDeCelda(ratio: number): number {
  return Math.round(anchoDeCelda(ratio) / ratioUsable(ratio));
}

/** Lo que mide una celda de esa proporción: es el `style` del nodo de React Flow. */
export const medidaDeCelda = (ratio: number): { width: number; height: number } => ({
  width: anchoDeCelda(ratio),
  height: altoDeCelda(ratio),
});

/**
 * Dónde arranca (esquina superior izquierda de su grilla) una tanda de `cantidad` celdas
 * entre lo que ya está colocado: debajo de su foto de origen si está en el lienzo, y si
 * no a la derecha de todo. Es lo que el lienzo guarda en la tanda al pedirla.
 */
export function lugarDeTanda(
  nodos: readonly NodoDelLienzo[],
  origenId: string | undefined,
  cantidad: number,
): Punto {
  const origen = origenId ? nodos.find((n) => n.id === origenId) : undefined;
  if (!origen) {
    const bordeDerecho = nodos.reduce((max, n) => Math.max(max, n.position.x + n.style.width), -Infinity);
    return { x: nodos.length === 0 ? 0 : bordeDerecho + SEPARACION_TANDAS, y: 0 };
  }
  const columnas = Math.max(1, Math.min(COLUMNAS_MAX, cantidad));
  // Nominal: las celdas de la tanda todavía no existen, así que no se sabe qué ancho va
  // a tener cada una. Alcanza para elegir la franja vertical en la que buscar estorbos.
  const anchoTanda = columnas * (ANCHO_CELDA + SEPARACION_CELDAS) - SEPARACION_CELDAS;
  const x = origen.position.x;
  // Debajo de la foto de origen: por debajo de TODO lo colocado que comparta la franja
  // horizontal de la tanda nueva (la foto misma, y lo que ya cayó debajo de ella).
  const y =
    nodos
      .filter((n) => n.position.x < x + anchoTanda && n.position.x + n.style.width > x)
      .reduce((max, n) => Math.max(max, n.position.y + n.style.height), -Infinity) + SEPARACION_TANDAS;
  return { x, y };
}

/**
 * Las celdas que hoy están donde el acomodo las puso (sin posición guardada), tal como se
 * ven: es lo que se guarda al primer arrastre para que dejen de recalcularse a partir de
 * lo que se mueve (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
 */
export function posicionesSueltas(
  celdas: readonly { id: string; position: Punto }[],
  guardadas: Posiciones,
): Posiciones {
  const out: Posiciones = {};
  for (const c of celdas) if (!guardadas[c.id]) out[c.id] = { x: c.position.x, y: c.position.y };
  return out;
}

/**
 * Despega las celdas que quedaron PISADAS. Las posiciones guardadas antes de que cada
 * celda tuviera su propio ancho (ADR-2026-09-22-las-celdas-del-lienzo-ocupan-la-misma-
 * superficie) asumían un paso fijo de 232 —220 más el aire—: con una apaisada de 328 las
 * fotos se montan una sobre otra, y `congelarSueltas` guarda ese paso viejo en cuanto
 * alguien arrastra algo, así que le pasa a cualquier lienzo ya tocado.
 *
 * Toca lo MÍNIMO: dentro de una fila —celdas con la misma `y`, que es la firma de la
 * grilla automática y no de algo que alguien puso a mano— cada una se corre a la derecha
 * lo justo para no pisar a la anterior. Lo que ya no se pisa no se mueve, así que volver
 * a pasar no hace nada y un lienzo acomodado a mano queda como está.
 */
function despegarFilas(nodos: NodoDelLienzo[]): void {
  const filas = new Map<number, NodoDelLienzo[]>();
  for (const n of nodos) {
    const fila = filas.get(n.position.y);
    if (fila) fila.push(n);
    else filas.set(n.position.y, [n]);
  }
  for (const fila of filas.values()) {
    if (fila.length < 2) continue;
    fila.sort((a, b) => a.position.x - b.position.x);
    for (let i = 1; i < fila.length; i += 1) {
      const anterior = fila[i - 1];
      const minimo = anterior.position.x + anterior.style.width + SEPARACION_CELDAS;
      // Posición NUEVA, no mutación: la de una celda guardada ES el objeto que vino en
      // `posiciones`, que pertenece al chat y no se toca.
      if (fila[i].position.x < minimo) fila[i].position = { x: minimo, y: fila[i].position.y };
    }
  }
}

export function acomodarLienzo(
  tandas: TandaDelLienzo[],
  posiciones: Posiciones,
  ratios: Ratios = {},
): NodoDelLienzo[] {
  const nodos: NodoDelLienzo[] = [];
  for (const t of tandas) {
    const columnas = Math.max(1, Math.min(COLUMNAS_MAX, t.celdas.length));
    let arranque = t.posicion;
    if (!arranque) {
      despegarFilas(nodos);
      arranque = lugarDeTanda(nodos, t.origenId, t.celdas.length);
    }
    const origenX = arranque.x;
    // El id de una celda con fila ES su `imageId` (`tandaDeCard`/`tandaDeGuardada` arman
    // `{ id, imageId: id }`), así que buscar por id de celda cubre las dos: las propias
    // (con `url`) reportan su ratio medido por SU id, que no colisiona con ningún imageId
    // (ADR-2026-09-17-el-lienzo-presta-tres-ranuras).
    const ratioDe = (celdaId: string): number => ratios[celdaId] || t.ratio;
    let yFila = arranque.y;
    for (let inicio = 0; inicio < t.celdas.length; inicio += columnas) {
      const fila = t.celdas.slice(inicio, inicio + columnas);
      const medidas = fila.map((c) => medidaDeCelda(ratioDe(c.id)));
      const altoFila = Math.max(...medidas.map((m) => m.height));
      // Cada columna arranca donde termina la anterior: sin un ancho común no hay paso
      // fijo. Avanza aunque la celda tenga posición guardada, para que la de al lado caiga
      // donde caería si nadie hubiera movido nada.
      let xColumna = origenX;
      fila.forEach((c, col) => {
        const ratio = ratioDe(c.id);
        const medida = medidas[col];
        // Una celda recién llegada hereda el lugar de su recuadro de carga: mientras
        // volaba se llamaba `${tanda}:pendiente:${índice}` y el usuario pudo moverlo
        // (ADR-2026-09-17-la-foto-aterriza-donde-quedo-su-recuadro).
        const position =
          posiciones[c.id] ??
          posiciones[idDePendiente(t.id, inicio + col)] ?? { x: xColumna, y: yFila };
        xColumna += medida.width + SEPARACION_CELDAS;
        nodos.push({
          id: c.id,
          type: 'celda',
          position,
          style: medida,
          data: {
            tandaId: t.id,
            imageId: c.imageId,
            esVideo: c.esVideo,
            ratio,
            creadaEn: t.creadaEn,
            titulo: c.titulo ?? t.titulo,
            ...(c.url ? { url: c.url } : {}),
          },
        });
      });
      yFila += altoFila + SEPARACION_CELDAS;
    }
  }
  despegarFilas(nodos);
  return nodos;
}
